import type { TabsProps as MuiTabsProps } from '@mui/material/Tabs';
import MuiTabs from '@mui/material/Tabs';
import { forwardRef } from 'react';

/**
 * Tabs — container that houses the tabs and owns focus / keyboard navigation.
 *
 * Thin wrapper over MUI `Tabs` that adds the brand `appearance` prop. Styled via the theme
 * (`MuiTabs` / `MuiTab` in components.ts). `appearance` maps to a root class the theme keys
 * off — we do NOT reuse MUI's `variant`, which is reserved for layout (standard|scrollable|
 * fullWidth).
 *
 * Sub-components:
 * - LOCAL: `Tab` (the tab element), `TabPanel` (custom, our own — no @mui/lab dependency).
 * - SHARED: `ButtonBase` (Tab's root — also used by Button, IconButton, MenuItem,
 *   ListItemButton, ToggleButton). Not modified by the Tabs theming.
 */
export interface TabsProps extends MuiTabsProps {
  /**
   * Visual style.
   * - `line` (default) — underline indicator beneath the selected tab.
   * - `outline` — segmented, top-rounded bordered tabs; selected = purple subtle fill + border.
   * - `panel` — flush square TILES divided by a hairline; selected = `surface.white` with the
   *   baseline cut away under it, so the tile and the surface below read as one shape. For a
   *   dashboard's metric strip, where each tab is a KPI and the selected one owns the panel
   *   beneath it. Pair it with `variant="fullWidth"` so the tiles share the row evenly.
   */
  appearance?: 'line' | 'outline' | 'panel';
}

export const Tabs = forwardRef<HTMLDivElement, TabsProps>(function Tabs(
  { appearance = 'line', className, ...props },
  ref
) {
  // One class per appearance; the theme keys every rule off it (`.AppTabs-outline &`, …).
  const appearanceClass = `AppTabs-${appearance}`;
  return <MuiTabs ref={ref} className={[className, appearanceClass].filter(Boolean).join(' ')} {...props} />;
});
